export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  domain: string; // Cognito hosted UI domain
  redirectUri: string;
  region: string;
}

export interface CognitoAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: IdTokenPayload | null;
  error: Error | null;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface IdTokenPayload {
  sub: string;
  email: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  iat: number;
  exp: number;
  aud: string;
  iss: string;
  [key: string]: any;
}

/**
 * Decode JWT token (no verification - backend verifies)
 */
function decodeToken(token: string): IdTokenPayload {
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid JWT token format");
  }

  const payload = parts[1];
  // Handle base64url encoding
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export class CognitoAuthService {
  private config: CognitoConfig;
  private refreshTimer: number | null = null;

  constructor(config: CognitoConfig) {
    this.config = config;
  }

  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(36).padStart(2, "0"))
      .join("")
      .slice(0, length);
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  private async startOAuthFlow(extraParams?: Record<string, string>) {
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateRandomString(32);

    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("oauth_state", state);

    const authUrl = new URL(`https://${this.config.domain}/oauth2/authorize`);
    authUrl.searchParams.set("client_id", this.config.clientId);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("scope", "email openid profile");
    authUrl.searchParams.set("redirect_uri", this.config.redirectUri);
    authUrl.searchParams.set("code_challenge", codeChallenge);
    authUrl.searchParams.set("code_challenge_method", "S256");
    authUrl.searchParams.set("state", state);

    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) {
        authUrl.searchParams.set(key, value);
      }
    }

    window.location.href = authUrl.toString();
  }

  /**
   * Start OAuth flow with specific IdP
   */
  async signInWithIdP(idpIdentifier: string) {
    await this.startOAuthFlow({ identity_provider: idpIdentifier });
  }

  /**
   * Start OAuth flow with Cognito hosted UI (IdP picker)
   */
  async signIn() {
    await this.startOAuthFlow();
  }

  /**
   * Handle OAuth callback - exchange code for tokens
   */
  async handleCallback(code: string, returnedState: string): Promise<CognitoTokens> {
    const storedState = sessionStorage.getItem("oauth_state");
    if (!storedState || storedState !== returnedState) {
      sessionStorage.removeItem("pkce_code_verifier");
      sessionStorage.removeItem("oauth_state");
      throw new Error("OAuth state mismatch — possible CSRF attack");
    }

    const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
    if (!codeVerifier) {
      throw new Error("Missing PKCE code verifier");
    }

    sessionStorage.removeItem("pkce_code_verifier");
    sessionStorage.removeItem("oauth_state");

    const tokenUrl = `https://${this.config.domain}/oauth2/token`;

    const params = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.statusText}`);
    }

    const data = await response.json();

    this.storeTokens({
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    });

    return {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  }

  /**
   * Get ID token (for GraphQL authorization header)
   * Supports both new format (cognito_id_token) and Amplify format
   */
  async getIdToken(): Promise<string | null> {
    // Check new format first
    let storedIdToken = localStorage.getItem("cognito_id_token");

    // Fall back to Amplify format: CognitoIdentityServiceProvider.{clientId}.{username}.idToken
    if (!storedIdToken) {
      const amplifyTokenKey = Object.keys(localStorage).find(
        (key) =>
          key.includes(`CognitoIdentityServiceProvider.${this.config.clientId}`) &&
          key.endsWith(".idToken")
      );

      if (amplifyTokenKey) {
        storedIdToken = localStorage.getItem(amplifyTokenKey);
      }
    }

    if (!storedIdToken) {
      return null;
    }

    try {
      const decoded = decodeToken(storedIdToken);
      const expiration = decoded.exp;

      // Check if token is expired or expiring soon (within 5 minutes)
      if (expiration > Date.now() / 1000 + 300) {
        return storedIdToken;
      }

      // Try to refresh (only works for new format tokens)
      const refreshed = await this.refreshSession();
      if (refreshed) {
        return refreshed.idToken;
      }
    } catch (error) {
      console.error("Token validation failed", error);
      this.clearTokens();
    }

    return null;
  }

  /**
   * Parse ID token claims
   */
  parseIdToken(idToken: string): IdTokenPayload {
    return decodeToken(idToken);
  }

  /**
   * Refresh session using refresh token
   */
  async refreshSession(): Promise<CognitoTokens | null> {
    const refreshToken = localStorage.getItem("cognito_refresh_token");
    if (!refreshToken) {
      return null;
    }

    const tokenUrl = `https://${this.config.domain}/oauth2/token`;

    const params = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: this.config.clientId,
      refresh_token: refreshToken,
    });

    try {
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data = await response.json();

      const tokens = {
        idToken: data.id_token,
        accessToken: data.access_token,
        refreshToken: refreshToken, // Cognito doesn't return new refresh token
      };

      this.storeTokens(tokens);
      return tokens;
    } catch (error) {
      console.error("Token refresh failed", error);
      this.clearTokens();
      return null;
    }
  }

  /**
   * Start auto-refresh timer
   */
  startRefreshTimer() {
    this.stopRefreshTimer();

    // Refresh every 50 minutes (tokens expire after 1 hour)
    this.refreshTimer = window.setInterval(() => {
      this.refreshSession().catch(console.error);
    }, 50 * 60 * 1000);
  }

  /**
   * Stop auto-refresh timer
   */
  stopRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Build the Cognito hosted-UI logout URL
   */
  buildLogoutUrl(): string {
    const logoutUrl = new URL(`https://${this.config.domain}/logout`);
    logoutUrl.searchParams.set("client_id", this.config.clientId);
    logoutUrl.searchParams.set("logout_uri", window.location.origin);
    return logoutUrl.toString();
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      this.stopRefreshTimer();
      this.clearTokens();
    } finally {
      // Redirect even if local cleanup throws — the hosted-UI session must still be terminated
      window.location.href = this.buildLogoutUrl();
    }
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(tokens: CognitoTokens) {
    localStorage.setItem("cognito_id_token", tokens.idToken);
    localStorage.setItem("cognito_access_token", tokens.accessToken);
    localStorage.setItem("cognito_refresh_token", tokens.refreshToken);
  }

  /**
   * Clear tokens from localStorage
   * Removes both the new format (cognito_*) and the Amplify format
   * (CognitoIdentityServiceProvider.{clientId}.*) that getIdToken falls back to
   */
  private clearTokens() {
    localStorage.removeItem("cognito_id_token");
    localStorage.removeItem("cognito_access_token");
    localStorage.removeItem("cognito_refresh_token");

    const amplifyKeyPrefix = `CognitoIdentityServiceProvider.${this.config.clientId}.`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(amplifyKeyPrefix))
      .forEach((key) => localStorage.removeItem(key));
  }
}
